import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isToday, isWithinInterval, parseISO, subDays } from "date-fns";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { displayDate } from "../../libs/dateFormat";
import { safeReplaceAll } from "../../libs/replace";

import { PushItem } from "../../types/member";

import { updatePushRead } from "../../services/memberServices";
import { PageableRequest } from "../../services/talkServices";

import useInfiniteScroll, {
  INFINITE_CODE,
} from "../../hooks/useInfiniteScroll";
import useFullScreenLoading from "../../hooks/useFullScreenLoading";
import useToTop from "../../hooks/useToTop";

import StickyHeader from "../Layout/StickyHeader";
import CommonPopup from "../Popup";
import CommonButton from "../Button";

import { ThreeDotIcon } from "../../pages/Talk/Detail";
import { PostListNoPostIcon } from "../Talk/PostList";

type Props = {
  icon: ReactNode;
  getListApi: (args: PageableRequest) => Promise<any>;
  unUseOnClick?: boolean;
};

export default function AlarmList({ icon, getListApi, unUseOnClick }: Props) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [alarmList, setAlarmList] = useState<PushItem[][]>([]);
  const scrollDivRef = React.useRef<HTMLDivElement>(null);
  const toTopComponent = useToTop({
    scrollDivRef,
    wrapperClassName: "!bottom-6 !z-30",
  });
  const [popup, setPopup] = useState<"alarmAction" | "">("");

  const classifyItemsByDate = (items: PushItem[]): PushItem[][] => {
    const today: PushItem[] = [];
    const thisWeek: PushItem[] = [];
    const previous: PushItem[] = [];

    items.forEach((item) => {
      // Replace '/' with '-' and ' ' with 'T' to make it an ISO 8601 format
      const isoDate = item.createdAt.replace(/\//g, "-").replace(" ", "T");
      const date = parseISO(isoDate);

      const now = new Date();
      const sevenDaysAgo = subDays(now, 7);

      const isWithinSevenDays = (dateToCheck: Date) => {
        return isWithinInterval(dateToCheck, { start: sevenDaysAgo, end: now });
      };

      if (isToday(date)) {
        today.push(item);
      } else if (isWithinSevenDays(date)) {
        thisWeek.push(item);
      } else {
        previous.push(item);
      }
    });

    return [today, thisWeek, previous];
  };

  const loadAlarm = async ({ page = 1, pageSize = 10 }: PageableRequest) => {
    let returnValue = INFINITE_CODE.ERROR;

    setIsLoading(true);
    try {
      const res = await getListApi({ page, pageSize });
      if (res.status === HttpStatusCode.Ok) {
        if (page === 1) {
          const list = classifyItemsByDate(res.data.items);
          setAlarmList(list);
        } else {
          const list = classifyItemsByDate(res.data.items);
          setAlarmList((prev) =>
            prev.map((prevList, index) => [...prevList, ...list[index]])
          );
        }
        returnValue = res.data.items.length;
      } else {
        // console.log(res);
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
    return returnValue;
  };

  const getHeaderFromIndex = (index: number) => {
    switch (index) {
      case 0:
        return "오늘";
      case 1:
        return "이번주";
      case 2:
        return "이전";
      default:
        return "";
    }
  };

  const [, { resetScroll }] = useInfiniteScroll({
    callback: async (args) => {
      return loadAlarm(args);
    },
    isLoading: isLoading,
  });

  useEffect(() => {
    resetScroll();
    loadAlarm({ page: 1, pageSize: 10 });
  }, []);

  return (
    <div ref={scrollDivRef}>
      {loadingComponent}
      <StickyHeader
        title="알림"
        onBackClick={() => navigate("/view")}
        rightContent={
          <div onClick={() => setPopup("alarmAction")}>
            <ThreeDotIcon />
          </div>
        }
        className="px-5"
      />

      {alarmList.reduce((prev, currentList) => {
        return prev + currentList.length;
      }, 0) > 0 ? (
        <div className="pt-2.5 flex flex-col gap-[30px] px-5">
          {alarmList.map((list, index) => {
            if (list.length === 0) return null;
            return (
              <div
                key={getHeaderFromIndex(index)}
                className="flex flex-col gap-2.5 text-brand-neutral-80 text-xs"
              >
                <div className="px-2.5 py-[5px] rounded-[10px] bg-brand-neutral-05">
                  {getHeaderFromIndex(index)}
                </div>
                {list.map((item) => (
                  <div
                    key={item.pushId}
                    className={clsx([
                      "p-5 flex gap-2.5 rounded-[10px]",
                      { "opacity-80": item.isRead === true },
                    ])}
                    onClick={async () => {
                      if (unUseOnClick) return;

                      if (item.isRead === false) {
                        await updatePushRead(item.pushId);
                      }
                      if (!item.url) {
                        navigate("/view");
                      }
                      navigate(
                        item.url
                          ?.replace("http://lifeapp.devsni.com", "")
                          .replace("app.devsni.com", "")
                          .replace("lifeapp.sandimall.com", "")
                          .replace("http://localhost:8080", "")
                      );
                    }}
                    style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.08)" }}
                  >
                    <div>{icon}</div>
                    <div className="flex flex-col gap-1">
                      <div className="text-base">{item.title}</div>
                      <div className="flex flex-col gap-2.5">
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              safeReplaceAll(item.content, "\n", "<br/>") ?? "",
                          }}
                        ></div>
                        <div>{displayDate(item.createdAt)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center w-full h-content gap-3 text-brand-neutral-60">
          <PostListNoPostIcon />
          알림이 없어요
        </div>
      )}

      {toTopComponent}

      <CommonPopup
        title="알림 활동"
        isOpen={popup === "alarmAction"}
        onClose={() => setPopup("")}
      >
        <CommonButton
          className="w-full"
          onClick={() => navigate("/myinfo/config")}
        >
          알림 설정
        </CommonButton>
      </CommonPopup>
    </div>
  );
}
